import { USAGE_CREDITS_CATEGORY_MAP } from "../../MyAccount/UsageMetrics/constants";
import {
  CATEGORY_TIER_TO_CODE,
  CREDIT_PURCHASE_CATEGORY_ORDER,
  CREDIT_PURCHASE_RATES,
  CREDIT_PURCHASE_TIER_FIELDS,
} from "./constants";

import {
  getSubscriptionTierUsage,
  parseCreditAmount,
  resolveUsageBillingMode,
} from "../../MyAccount/UsageMetrics/utils";

const createEmptyTierCell = () => ({ count: 0, cost: 0 });

const resolveCategoryMapping = (code, categoryName) => {
  if (USAGE_CREDITS_CATEGORY_MAP[code]) {
    return USAGE_CREDITS_CATEGORY_MAP[code];
  }

  const [prefix, ...tierParts] = String(code || "").split("_");
  const prefixKey = {
    CHEM: "chemistry",
    MICRO: "microbiology",
  }[prefix];
  const tierName = tierParts.join(" ").toLowerCase() || "basic";
  const normalizedTier = CREDIT_PURCHASE_TIER_FIELDS.find((tier) => tierName.includes(tier)) || "basic";
  const displayName = String(categoryName || "").replace(/\s+(Basic|Advanced|Specialty)$/i, "").trim();

  if (!prefixKey) {
    return {
      categoryKey: `unmapped-${code}`,
      categoryName: displayName || code,
      tierName: "Basic",
    };
  }

  return {
    categoryKey: prefixKey,
    categoryName: displayName || prefixKey,
    tierName: normalizedTier.charAt(0).toUpperCase() + normalizedTier.slice(1),
  };
};

const getClassifySamples = (classifyResponse) => (
  classifyResponse?.result?.samples ?? classifyResponse?.samples ?? []
);

export const getClassifyCreditCheckError = (classifyResponse, requestedSamples = []) => {
  const requestHadTests = requestedSamples.some((sample) => (
    Array.isArray(sample?.tests) && sample.tests.some(Boolean)
  ));
  const classifiedSamples = classifyResponse?.result?.samples ?? classifyResponse?.samples;

  if (requestHadTests && (!Array.isArray(classifiedSamples) || classifiedSamples.length === 0)) {
    return "Unable to classify tests.";
  }

  const classifiedTestList = (Array.isArray(classifiedSamples) ? classifiedSamples : []).flatMap(
    (sample) => sample?.tests || [],
  );
  const hasIneligibleTest = classifiedTestList.some((test) => test?.eligible !== true);
  const hasUncodedEligibleTest = classifiedTestList.some(
    (test) => test?.eligible === true && !test?.usage_category_code,
  );
  if (hasIneligibleTest || hasUncodedEligibleTest) {
    return "Unable to classify tests.";
  }

  return "";
};

const getPrepaidRemainingCredits = (credit) => {
  const balance = credit?.total_credits ?? credit?.totalCredits;
  if (balance == null || balance === "") {
    return getSubscriptionTierUsage(credit, { billingMode: "prepaid" }).remaining;
  }

  return parseCreditAmount(balance);
};

const getSpendableRemainingCredits = (credit, billingMode) => {
  if (billingMode === "prepaid") {
    return getPrepaidRemainingCredits(credit);
  }

  const subscriptionRemaining = getSubscriptionTierUsage(credit, {
    billingMode: "subscription",
    topupPlan: false,
  }).remaining;
  const topupRemaining = getSubscriptionTierUsage(credit, {
    billingMode: "subscription",
    topupPlan: true,
  }).remaining;

  return subscriptionRemaining + topupRemaining;
};

export const getAvailableCreditsByCode = (creditsResponse) => {
  const credits = creditsResponse?.credits ?? [];
  const billingMode = resolveUsageBillingMode(creditsResponse?.customer_type);

  return credits.reduce((acc, credit) => {
    const code = credit?.usage_category_code;
    if (!code) return acc;

    acc[code] = getSpendableRemainingCredits(credit, billingMode);
    return acc;
  }, {});
};

export const formatCreditCurrency = (amount, minimumFractionDigits) => (
  `$${Number(amount || 0).toLocaleString(undefined, {
    minimumFractionDigits,
    maximumFractionDigits: 2,
  })}`
);

export const getTierCellValue = (row, tier, rates = CREDIT_PURCHASE_RATES) => {
  const value = row?.[tier];
  if (value && typeof value === "object") {
    return {
      count: Number(value.count) || 0,
      cost: Number(value.cost) || 0,
    };
  }

  return {
    count: Number(value) || 0,
    cost: Number(rates?.[tier]) || 0,
  };
};

export const getRowTotalPrice = (row, rates = CREDIT_PURCHASE_RATES) => (
  CREDIT_PURCHASE_TIER_FIELDS.reduce((sum, tier) => {
    const { count, cost } = getTierCellValue(row, tier, rates);
    return sum + (count * cost);
  }, 0)
);

export const getCreditPurchaseTotal = (rows = [], rates = CREDIT_PURCHASE_RATES) => (
  rows.reduce((sum, row) => sum + getRowTotalPrice(row, rates), 0)
);

export const buildCreditPurchaseRowsFromClassify = (classifyResponse, options = {}) => {
  const samples = getClassifySamples(classifyResponse);
  if (!Array.isArray(samples) || !samples.length) {
    return null;
  }

  const availableCreditsByCode = options.availableCreditsByCode;
  const groups = new Map();
  samples.forEach((sample) => {
    (sample.tests || []).forEach((test) => {
      if (test?.eligible !== true) return;

      const code = test?.usage_category_code;
      const name = test?.usage_category_name;
      if (!code) return;

      const testCost = Number(test.cost) || 0;
      const existing = groups.get(code);
      if (existing) {
        existing.count += 1;
        existing.totalCost += testCost;
        return;
      }

      groups.set(code, {
        code,
        name,
        count: 1,
        totalCost: testCost,
      });
    });
  });

  if (availableCreditsByCode) {
    groups.forEach((group, code) => {
      const shortfall = Math.max(0, group.count - (Number(availableCreditsByCode[code]) || 0));
      if (shortfall === 0) {
        groups.delete(code);
        return;
      }
      if (group.count > 0 && shortfall < group.count) {
        group.totalCost = (group.totalCost / group.count) * shortfall;
      }
      group.count = shortfall;
    });
  }

  groups.forEach((group) => {
    group.cost = group.count > 0 ? group.totalCost / group.count : 0;
  });

  if (!groups.size) {
    return null;
  }

  const rowsByCategory = {};
  groups.forEach((group) => {
    const mapping = resolveCategoryMapping(group.code, group.name);
    const categoryKey = mapping.categoryKey;
    const tier = mapping.tierName.toLowerCase();

    if (!rowsByCategory[categoryKey]) {
      rowsByCategory[categoryKey] = {
        key: categoryKey,
        name: mapping.categoryName,
        basic: createEmptyTierCell(),
        advanced: createEmptyTierCell(),
        specialty: createEmptyTierCell(),
      };
    }

    if (!CREDIT_PURCHASE_TIER_FIELDS.includes(tier)) {
      return;
    }

    const existingCell = rowsByCategory[categoryKey][tier];
    const nextCount = existingCell.count + group.count;
    const nextPrice = (existingCell.count * existingCell.cost) + (group.count * group.cost);
    rowsByCategory[categoryKey][tier] = {
      count: nextCount,
      cost: nextCount > 0 ? nextPrice / nextCount : 0,
      sources: [
        ...(existingCell.sources || []),
        { code: group.code, count: group.count },
      ],
    };
  });

  return CREDIT_PURCHASE_CATEGORY_ORDER
    .filter((key) => rowsByCategory[key])
    .map((key) => rowsByCategory[key])
    .concat(
      Object.values(rowsByCategory).filter((row) => !CREDIT_PURCHASE_CATEGORY_ORDER.includes(row.key)),
    );
};

export const buildCheckoutCreditsFromPurchaseRows = (rows = []) => {
  const credits = [];

  rows.forEach((row) => {
    CREDIT_PURCHASE_TIER_FIELDS.forEach((tier) => {
      const rawCell = row?.[tier];
      const { count } = getTierCellValue(row, tier);
      if (count <= 0) return;

      const sources = rawCell && typeof rawCell === "object" && Array.isArray(rawCell.sources)
        ? rawCell.sources
        : null;
      if (sources?.length) {
        sources.forEach((source) => {
          if (source.code && source.count > 0) {
            credits.push({
              usage_category_code: source.code,
              quantity: source.count,
            });
          }
        });
        return;
      }

      const code = CATEGORY_TIER_TO_CODE[`${row.key}-${tier}`];
      if (code) {
        credits.push({
          usage_category_code: code,
          quantity: count,
        });
      }
    });
  });

  return credits;
};

export const getCreditPurchaseTotalCredits = (rows = []) => (
  rows.reduce((sum, row) => (
    sum + CREDIT_PURCHASE_TIER_FIELDS.reduce((tierSum, tier) => (
      tierSum + getTierCellValue(row, tier).count
    ), 0)
  ), 0)
);
